import { mergeStyles } from '@bulknutrients/bulk-shared-lib/utils';
import { Icons } from '@components/Icons/Icons';
import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import {
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { twJoin } from 'tailwind-merge';

export function EmblaCarousel({
  slides,
  title,
  titleClass,
}: {
  slides: ReactNode[];
  title?: string;
  titleClass?: string;
}) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: 'start',
  });

  const [slidesInView, setSlidesInView] = useState<number[] | undefined>(
    emblaApi?.slidesInView()
  );

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  useEffect(() => {
    // this is to trigger slidesInView to update opacity on relevant cards in accordions and carousels
    if (!emblaApi) return;

    const updateSlidesInView = () => setSlidesInView(emblaApi.slidesInView());

    emblaApi.reInit();

    emblaApi.on('slidesChanged', updateSlidesInView);
    emblaApi.on('slidesInView', updateSlidesInView);
    emblaApi.on('scroll', updateSlidesInView);
    emblaApi.on('init', updateSlidesInView);

    return () => {
      emblaApi.off('slidesChanged', updateSlidesInView);
      emblaApi.off('scroll', updateSlidesInView);
      emblaApi.off('slidesInView', updateSlidesInView);
      emblaApi.off('init', updateSlidesInView);
    };
  }, [emblaApi]);

  return slides.length > 0 ? (
    <div className={twJoin(['embla', 'overflow-hidden !m-0 px-4'])}>
      {/* Override embla x-margin and swap for padding to scale effect doesn't get cut off */}
      <div className="flex justify-between flex-row mb-4 mr-4">
        <div
          className={twJoin([
            titleClass ?? '',
            'uppercase text-black dark:text-white text-xl w-3/4',
          ])}
        >
          {title ?? ''}
        </div>
        <div>
          <button
            type="button"
            className={twJoin([
              'embla__prev',
              'mr-4 dark:text-grey-light text-grey-darkest',
            ])}
            onClick={scrollPrev}
          >
            <Icons.Chevron.Left size={20} />
          </button>
          <button
            type="button"
            className={twJoin([
              'embla__next',
              'dark:text-grey-light text-grey-darkest',
            ])}
            onClick={scrollNext}
          >
            <Icons.Chevron.Right size={20} />
          </button>
        </div>
      </div>
      <div className={twJoin(['embla__viewport', 'relative'])} ref={emblaRef}>
        <div
          className={twJoin([
            'embla__container',
            'flex items-stretch gap-5 py-4',
          ])}
        >
          {slides.map((slide, idx) => (
            <div
              key={idx}
              className={twJoin([
                'embla__slide',
                'grow-0 shrink-0 flex last:pr-5 transition-all duration-300',
                (idx === slidesInView?.[slidesInView.length - 1] &&
                  idx !== slides.length - 1 &&
                  slidesInView.length !== 1) ||
                !slidesInView?.includes(idx)
                  ? 'opacity-50'
                  : 'opacity-100',
              ])}
            >
              {slide}
            </div>
          ))}
        </div>
      </div>
    </div>
  ) : null;
}

export function QuickCartCarousel({
  slides,
  headingText,
}: {
  slides: ReactNode[];
  headingText: string;
}): ReactElement {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [isMobile, setIsMobile] = useState(false);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  // Check if mobile device and only show arrows if not on mobile
  useEffect(() => {
    const isMobile = window.navigator.maxTouchPoints > 0;
    setIsMobile(isMobile);
  }, []);

  const fadeTo = 'to-white-transparent dark:to-black-transparent';

  return slides.length > 0 ? (
    <div className={twJoin(['embla', ''])}>
      <div className="flex items-center min-h-12">
        <span className="border-b border-grey-mid m-2 w-1" />
        <span className="uppercase font-bold text-sm">{headingText}</span>
        <span className="border-b border-grey-mid grow m-2" />
        {!isMobile && (
          <>
            <button
              type="button"
              className={twJoin(['embla__prev', 'w-12 h-12 text-grey-dark'])}
              onClick={scrollPrev}
            >
              <Icons.Chevron.Left size={20} />
            </button>
            <button
              type="button"
              className={twJoin(['embla__next', 'w-12 h-12 text-grey-dark'])}
              onClick={scrollNext}
            >
              <Icons.Chevron.Right size={20} />
            </button>
          </>
        )}
      </div>
      <div className={twJoin(['embla__viewport', 'relative'])} ref={emblaRef}>
        <div
          className={twJoin(['embla__container', 'flex items-stretch gap-2'])}
        >
          {slides.map((slide, idx) => (
            <div
              key={idx}
              className={twJoin([
                'embla__slide',
                'grow-0 shrink-0 basis-[90%] last:pr-2',
              ])}
            >
              {slide}
            </div>
          ))}
        </div>
        <div
          className={mergeStyles(
            fadeTo ?? '',
            'pointer-events-none absolute right-0 top-0 h-full w-[10%] bg-linear-to-r from-transparent from-0% to-orange to-100%'
          )}
        />
        <div
          className={mergeStyles(
            fadeTo ?? '',
            'pointer-events-none absolute left-0 top-0 h-full w-[10%] bg-linear-to-l from-transparent from-0% to-orange to-100%'
          )}
        />
      </div>
    </div>
  ) : (
    <></>
  );
}

export function AutoPlayCarousel({
  slides,
  delay,
}: {
  slides: ReactNode[];
  delay?: number;
}) {
  const [emblaRef] = useEmblaCarousel(
    {
      loop: true,
    },
    [
      Autoplay({
        playOnInit: true,
        stopOnInteraction: false,
        stopOnMouseEnter: true,
        delay: delay ?? 8000,
      }),
    ]
  );

  return slides.length > 0 ? (
    <div className={twJoin(['embla', 'relative'])}>
      <div
        className={twJoin(['embla__viewport', 'overflow-hidden'])}
        ref={emblaRef}
      >
        <div className={twJoin(['embla__container', 'flex'])}>
          {slides.map((slide, idx) => (
            <div
              key={idx}
              className="embla__slide w-full basis-full grow shrink-0"
            >
              {slide}
            </div>
          ))}
        </div>
      </div>
    </div>
  ) : null;
}
